import React from 'react';
import Icon from '../../../components/AppIcon';

const ProjectionPanel = ({ projectionData }) => {
  if (!projectionData) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center">
        <div className="flex flex-col items-center gap-4">
          <div className="flex items-center justify-center w-16 h-16 rounded-full bg-muted">
            <Icon name="Calculator" size={32} className="text-muted-foreground" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground mb-2">No Projections Yet</h3>
            <p className="text-sm text-muted-foreground max-w-md">
              Enter your retirement details and click "Calculate Retirement Plan" to see your personalized projections
            </p>
          </div>
        </div>
      </div>
    );
  }

  const formatIndianCurrency = (amount) => {
    return '₹' + amount?.toLocaleString('en-IN', { maximumFractionDigits: 0 });
  };

  const savingsPercentage = ((projectionData?.monthlySavingsRequired / projectionData?.currentIncome) * 100)?.toFixed(1);

  return (
    <div className="bg-gradient-to-br from-primary/5 to-accent/5 border border-primary/20 rounded-lg p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary">
          <Icon name="TrendingUp" size={20} className="text-white" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">Your Retirement Projection</h3>
          <p className="text-sm text-muted-foreground">Based on your inputs and Indian market conditions</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-card rounded-lg p-4 border border-border">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Wallet" size={16} className="text-primary" />
            <p className="text-xs font-medium text-muted-foreground">Required Corpus</p>
          </div>
          <p className="text-2xl font-bold text-foreground">{formatIndianCurrency(projectionData?.corpusNeeded)}</p>
          <p className="text-xs text-muted-foreground mt-1">Total amount needed at retirement</p>
        </div>

        <div className="bg-card rounded-lg p-4 border border-border">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="PiggyBank" size={16} className="text-accent" />
            <p className="text-xs font-medium text-muted-foreground">Monthly Savings Required</p>
          </div>
          <p className="text-2xl font-bold text-foreground">{formatIndianCurrency(projectionData?.monthlySavingsRequired)}</p>
          <p className="text-xs text-muted-foreground mt-1">{savingsPercentage}% of current income</p>
        </div>

        <div className="bg-card rounded-lg p-4 border border-border">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Calendar" size={16} className="text-primary" />
            <p className="text-xs font-medium text-muted-foreground">Years to Retirement</p>
          </div>
          <p className="text-2xl font-bold text-foreground">{projectionData?.yearsToRetirement}</p>
          <p className="text-xs text-muted-foreground mt-1">Time to build your corpus</p>
        </div>

        <div className="bg-card rounded-lg p-4 border border-border">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="TrendingUp" size={16} className="text-accent" />
            <p className="text-xs font-medium text-muted-foreground">Future Monthly Income</p>
          </div>
          <p className="text-2xl font-bold text-foreground">{formatIndianCurrency(projectionData?.futureMonthlyIncome)}</p>
          <p className="text-xs text-muted-foreground mt-1">Inflation-adjusted at retirement</p>
        </div>
      </div>
      <div className="bg-card rounded-lg p-4 border border-border">
        <div className="flex items-start gap-3">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-success/10 flex-shrink-0">
            <Icon name="CheckCircle2" size={16} className="text-success" />
          </div>
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-foreground mb-2">Retirement Readiness Assessment</h4>
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Retirement Duration</span>
                <span className="font-medium text-foreground">{projectionData?.yearsInRetirement} years</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Savings Rate</span>
                <span className={`font-medium ${parseFloat(savingsPercentage) > 30 ? 'text-warning' : 'text-success'}`}>
                  {savingsPercentage}%
                </span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Status</span>
                <span className="font-medium text-success">Plan Generated</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {parseFloat(savingsPercentage) > 30 && (
        <div className="mt-4 p-3 bg-warning/10 border border-warning/20 rounded-lg">
          <div className="flex items-start gap-2">
            <Icon name="AlertTriangle" size={16} className="text-warning mt-0.5 flex-shrink-0" />
            <p className="text-xs text-warning">
              Your required savings rate is high. Consider adjusting your retirement age, expected lifestyle, or exploring additional income sources.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectionPanel;